import { Badge, Group, Text } from "@mantine/core";
import { IChecklistItemRow, IChecklistPopulated } from "@mctypes/checklist.types";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import React from "react";

export const DEFAULT_CHECKLIST_COL_DEF: ColDef<IChecklistItemRow> = {
  filter: true,
  resizable: true,
  sortable: true,
};

export const getChecklistColumns = (
  activeCheckKeys: string[],
  checkTypes: IChecklistPopulated["checkTypes"] | undefined,
  hiddenColumns: string[],
): ColDef<IChecklistItemRow>[] => {
  const checkColumns: ColDef<IChecklistItemRow>[] = activeCheckKeys.map((checkKey) => ({
    colId: checkKey,
    headerName: checkTypes?.[checkKey]?.name ?? checkKey,
    width: 135,
    valueGetter: (params) => {
      if (params.data?.originalItem == null) return "";
      return params.data?.checks?.[checkKey]?.status ?? "-";
    },
    cellRenderer: CheckStatusCell,
    hide: hiddenColumns.includes(checkKey),
  }));

  return [
    {
      colId: "rowId",
      headerName: "ID",
      field: "rowId",
      width: 100,
      pinned: "left",
      sort: "asc",
    },
    {
      colId: "title",
      headerName: "Tittel",
      field: "title",
      minWidth: 260,
      flex: 2,
      pinned: "left",
      cellRenderer: TitleCell,
    },
    {
      colId: "reference",
      headerName: "Referanse",
      field: "reference",
      width: 150,
      hide: hiddenColumns.includes("reference"),
    },
    ...checkColumns,
    {
      colId: "comment",
      headerName: "Kommentar",
      field: "comment",
      minWidth: 220,
      flex: 1,
      valueGetter: (params) => params.data?.comment ?? "",
      hide: hiddenColumns.includes("comment"),
    },
    {
      colId: "connectedIssues",
      headerName: "Saker",
      width: 110,
      valueGetter: (params) => params.data?.connectedIssues?.length ?? "",
      hide: hiddenColumns.includes("connectedIssues"),
    },
  ];
};

const TitleCell: React.FC<ICellRendererParams<IChecklistItemRow>> = ({ data }) => {
  if (data == null) return null;

  return (
    <Group gap="xs" wrap="nowrap" h="100%">
      {data.originalItem == null && <Badge variant="light">Kategori</Badge>}
      <Text fw={data.originalItem == null ? 700 : 400} truncate>
        {data.title}
      </Text>
    </Group>
  );
};

const CheckStatusCell: React.FC<ICellRendererParams<IChecklistItemRow>> = ({ value }) => {
  if (value == null || value === "") return null;

  const color =
    value === "YES"
      ? "green"
      : value === "NO"
      ? "red"
      : value === "IR" || value === "UAV"
      ? "yellow"
      : value === "DISABLED"
      ? "gray"
      : "lightgray";

  return (
    <Group h="100%" align="center">
      <Badge color={color} variant="light">
        {value}
      </Badge>
    </Group>
  );
};
